import { ADMISSIONS_STEPS } from "core/consts";
import { useIntParams } from "core/model/utils/urls";
import { AdmissionsStep } from "core/types";
import { Step } from "ds/ui/stepper/stepper";
import { useCareproviderNavigationHandlers } from "dsl/hooks/useNavigationHandlers";
import { createContext, ReactNode, useContext } from "react";
import { useTranslations } from "translations";
import { useAdmissionsContext } from "../AdmissionsContext";

type AdmissionsStepsContextType = {
  currentStep: AdmissionsStep;
  goBack: () => void;
  goForward: () => void;
  steps: Record<AdmissionsStep, Step>;
};

const AdmissionsStepsContext = createContext<
  AdmissionsStepsContextType | undefined
>(undefined);

type AdmissionsStepsProviderProps = {
  children: ReactNode;
};

export function AdmissionsStepsProvider({
  children,
}: AdmissionsStepsProviderProps) {
  const translations = useTranslations();
  const { step } = useIntParams<{ step: AdmissionsStep }>();
  const { admissionsId, admissionsPayload, isPreview } = useAdmissionsContext();
  const navigation = useCareproviderNavigationHandlers();

  const steps = {
    [ADMISSIONS_STEPS.CARE_INFORMATION]: {
      ["aria-label"]:
        translations.careproviderApp.recareAdmissionsForm.stepper.ariaStepThree,
    },
    [ADMISSIONS_STEPS.DESIRED_CARE]: {
      ["aria-label"]:
        translations.careproviderApp.recareAdmissionsForm.stepper.ariaStepTwo,
    },
    [ADMISSIONS_STEPS.FURTHER_INFORMATION]: {
      ["aria-label"]:
        translations.careproviderApp.recareAdmissionsForm.stepper.ariaStepFour,
    },
    [ADMISSIONS_STEPS.PERSONAL_INFORMATION]: {
      ["aria-label"]:
        translations.careproviderApp.recareAdmissionsForm.stepper.ariaStepOne,
    },
  };

  return (
    <AdmissionsStepsContext.Provider
      value={{
        currentStep: step,
        goBack: () => {
          const prevStep =
            step > ADMISSIONS_STEPS.PERSONAL_INFORMATION
              ? ((step - 1) as AdmissionsStep)
              : step;

          if (isPreview) {
            navigation.goToAdmissionsFormPreview({
              formType: admissionsPayload.form_type,
              step: prevStep,
            });
            return;
          }
          navigation.goToAdmissionsForm({
            admissionsId,
            step: prevStep,
          });
        },
        goForward: () => {
          const nextStep =
            step < ADMISSIONS_STEPS.FURTHER_INFORMATION
              ? ((step + 1) as AdmissionsStep)
              : step;

          if (isPreview) {
            navigation.goToAdmissionsFormPreview({
              formType: admissionsPayload.form_type,
              step: nextStep,
            });
            return;
          }
          navigation.goToAdmissionsForm({
            admissionsId,
            step: nextStep,
          });
        },
        steps,
      }}
    >
      {children}
    </AdmissionsStepsContext.Provider>
  );
}

export function useAdmissionsSteps() {
  const context = useContext(AdmissionsStepsContext);
  if (context === undefined) {
    throw new Error(
      "useAdmissionsSteps must be used within an AdmissionsStepsProvider",
    );
  }
  return context;
}
